import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Factory, Globe, Truck, BadgeCheck } from "lucide-react";
import { UNITS } from "@/data/units";

const tiles = UNITS.map((u) => ({ image: u.heroImage }));

const Advantage = ({ Icon, text, testId }) => (
  <div
    data-testid={testId}
    className="flex flex-col items-center text-center min-w-0"
  >
    <div className="h-12 w-12 flex items-center justify-center mb-3">
      <Icon
        size={42}
        strokeWidth={1.4}
        style={{ color: "var(--emka-gold)" }}
        aria-hidden
      />
    </div>
    <p className="text-[11.5px] sm:text-xs font-semibold tracking-wider uppercase text-white/85 leading-snug max-w-[140px]">
      {text}
    </p>
  </div>
);

const Hero = () => {
  const { t } = useLang();

  return (
    <section
      data-testid="hero-section"
      className="relative pt-[112px] lg:pt-[124px] bg-emka"
    >
      {/* Collage band */}
      <div className="relative w-full h-[300px] sm:h-[360px] lg:h-[460px] overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-5">
          {tiles.map((tile, i) => (
            <div
              key={i}
              className="hero-tile"
              style={{ marginLeft: i === 0 ? "-3%" : "-3%" }}
            >
              <div
                className="hero-tile-inner h-full"
                style={{ backgroundImage: `url(${tile.image})` }}
              />
              {/* dark fade */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(11,16,32,0.10) 0%, rgba(11,16,32,0.65) 100%)",
                }}
              />
            </div>
          ))}
        </div>

        {/* Headline overlay */}
        <div className="absolute inset-0">
          <div className="max-w-[1400px] mx-auto h-full px-4 sm:px-6 lg:px-10 flex items-center">
            <div className="max-w-2xl">
              <h1
                className="font-display font-extrabold uppercase leading-[1.02] text-white rise"
                style={{ fontSize: "clamp(2rem, 5vw, 4.2rem)" }}
              >
                <span className="block">{t.hero.line1}</span>
                <span className="block" style={{ color: "var(--emka-gold)" }}>
                  {t.hero.line2}
                </span>
                <span className="block">{t.hero.line3}</span>
              </h1>

              <div className="mt-6 flex items-start gap-4 rise rise-2">
                <span
                  className="mt-1 block w-[3px] self-stretch"
                  style={{
                    backgroundColor: "var(--emka-gold)",
                    minHeight: "44px",
                  }}
                />
                <div className="text-white/90 text-sm sm:text-base leading-relaxed">
                  <div>{t.hero.sub1}</div>
                  <div>{t.hero.sub2}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advantage bar */}
      <div className="bg-emka">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-14">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-4 sm:gap-x-6">
            <Advantage
              Icon={Factory}
              text={t.hero.adv1}
              testId="adv-production"
            />
            <Advantage
              Icon={Globe}
              text={t.hero.adv2}
              testId="adv-sales-office"
            />
            <Advantage Icon={Truck} text={t.hero.adv3} testId="adv-logistics" />
            <Advantage
              Icon={BadgeCheck}
              text={t.hero.adv4}
              testId="adv-quality"
            />
          </div>

          {/* Certifications strip */}
          <div
            data-testid="cert-strip"
            className="mt-8 pt-6 border-t border-white/8 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
          >
            {["ISO 9001:2015", "ISO 22000:2005", "CE Certified"].map((c) => (
              <span
                key={c}
                data-testid={`cert-${c.split(" ")[0].toLowerCase()}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10.5px] font-semibold tracking-[0.16em] uppercase border"
                style={{
                  color: "var(--emka-gold)",
                  borderColor: "rgba(212,162,73,0.35)",
                  backgroundColor: "rgba(212,162,73,0.06)",
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: "var(--emka-gold)" }}
                />
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
